/**
 * Scene transition renderers. Pure 2D canvas: each draws the outgoing scene
 * `a` and incoming scene `b` (both already rendered at output size) into
 * `ctx` at eased progress `p` (0 = all A, 1 = all B).
 *
 * `direction` is the direction of MOTION: 'left' (the default) means the
 * incoming scene travels leftwards, entering from the right edge.
 *
 * Renderers only use the small `TransitionContext` subset of the 2D API, so
 * they are testable with a recording fake.
 */

import type { AnimationDirection, TransitionId } from '@canvas/shared-types';

export type TransitionContext = Pick<
  CanvasRenderingContext2D,
  | 'save'
  | 'restore'
  | 'drawImage'
  | 'fillRect'
  | 'beginPath'
  | 'rect'
  | 'clip'
  | 'globalAlpha'
  | 'fillStyle'
>;

export interface TransitionOptions {
  width: number;
  height: number;
  direction?: AnimationDirection;
  /** dip-to-color only. */
  color?: string;
}

export type TransitionRenderer = (
  ctx: TransitionContext,
  a: CanvasImageSource,
  b: CanvasImageSource,
  p: number,
  opts: TransitionOptions
) => void;

/** Scaled passes for zoom-blur: few enough to stay cheap, enough to read as motion. */
const ZOOM_BLUR_PASSES = 4;
const ZOOM_BLUR_MAX_SCALE = 0.12;

function clamp01(p: number): number {
  return Math.min(1, Math.max(0, p));
}

/** Unit vector of travel. */
function motion(dir: AnimationDirection | undefined): { x: number; y: number } {
  switch (dir) {
    case 'right':
      return { x: 1, y: 0 };
    case 'up':
      return { x: 0, y: -1 };
    case 'down':
      return { x: 0, y: 1 };
    case 'left':
    case 'center':
    case undefined:
    default:
      return { x: -1, y: 0 };
  }
}

function draw(
  ctx: TransitionContext,
  img: CanvasImageSource,
  opts: TransitionOptions,
  dx = 0,
  dy = 0,
  alpha = 1
): void {
  ctx.globalAlpha = alpha;
  ctx.drawImage(img, dx, dy, opts.width, opts.height);
  ctx.globalAlpha = 1;
}

const cut: TransitionRenderer = (ctx, _a, b, _p, opts) => draw(ctx, b, opts);

const crossfade: TransitionRenderer = (ctx, a, b, p, opts) => {
  draw(ctx, a, opts);
  draw(ctx, b, opts, 0, 0, clamp01(p));
};

const dipToColor: TransitionRenderer = (ctx, a, b, p, opts) => {
  const t = clamp01(p);
  // First half fades A to the color, second half fades the color to B.
  draw(ctx, t < 0.5 ? a : b, opts);
  ctx.globalAlpha = t < 0.5 ? t * 2 : (1 - t) * 2;
  ctx.fillStyle = opts.color || '#000000';
  ctx.fillRect(0, 0, opts.width, opts.height);
  ctx.globalAlpha = 1;
};

const slide: TransitionRenderer = (ctx, a, b, p, opts) => {
  const v = motion(opts.direction);
  const rest = 1 - clamp01(p);
  draw(ctx, a, opts);
  draw(ctx, b, opts, -v.x * opts.width * rest, -v.y * opts.height * rest);
};

const push: TransitionRenderer = (ctx, a, b, p, opts) => {
  const v = motion(opts.direction);
  const t = clamp01(p);
  draw(ctx, a, opts, v.x * opts.width * t, v.y * opts.height * t);
  draw(ctx, b, opts, -v.x * opts.width * (1 - t), -v.y * opts.height * (1 - t));
};

const wipe: TransitionRenderer = (ctx, a, b, p, opts) => {
  const v = motion(opts.direction);
  const t = clamp01(p);
  const { width: w, height: h } = opts;
  draw(ctx, a, opts);
  // The revealed region grows from the edge B enters from.
  let x = 0;
  let y = 0;
  let rw = w;
  let rh = h;
  if (v.x < 0) {
    x = w * (1 - t);
    rw = w * t;
  } else if (v.x > 0) {
    rw = w * t;
  } else if (v.y < 0) {
    y = h * (1 - t);
    rh = h * t;
  } else {
    rh = h * t;
  }
  if (rw <= 0 || rh <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, rw, rh);
  ctx.clip();
  draw(ctx, b, opts);
  ctx.restore();
};

const zoomBlur: TransitionRenderer = (ctx, a, b, p, opts) => {
  const t = clamp01(p);
  draw(ctx, a, opts);
  draw(ctx, b, opts, 0, 0, t);
  // Streak strongest mid-transition, on whichever scene dominates.
  const strength = Math.sin(Math.PI * t);
  if (strength <= 0.01) return;
  const src = t < 0.5 ? a : b;
  for (let i = 1; i <= ZOOM_BLUR_PASSES; i += 1) {
    const s = 1 + (ZOOM_BLUR_MAX_SCALE * strength * i) / ZOOM_BLUR_PASSES;
    const w = opts.width * s;
    const h = opts.height * s;
    ctx.globalAlpha = (0.35 * strength) / i;
    ctx.drawImage(src, (opts.width - w) / 2, (opts.height - h) / 2, w, h);
  }
  ctx.globalAlpha = 1;
};

export const TRANSITION_RENDERERS: Record<TransitionId, TransitionRenderer> = {
  none: cut,
  cut,
  crossfade,
  'dip-to-color': dipToColor,
  slide,
  push,
  wipe,
  'zoom-blur': zoomBlur,
};
